import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchTrip, setChecklistDone, setDeadlineDone, moveEvent, deleteEvent, deleteVisit,
  fetchMembers, inviteMember, removeMember,
} from '../lib/api.js';
import {
  buildDays, buildToday, legsByParent, stopForDate,
  dayLabel, dayShort, dayOfWeek, rangeLabel,
  travellerZone, zoneDate, zoneTime, zoneLabel, untilLabel, spanLabel,
  money, localAmount, derivation, PAYMENT, KIND, telHref,
  mapsRouteHref, mappablePlaces, localTime, deletePrompt, setHomeCurrency, ageOn,
} from '../lib/format.js';
import EventCard, { Block, DocRow, NotesOpen, VisitCard } from '../components/EventCard.jsx';
import History from '../components/History.jsx';
import Icon, { KIND_ICON } from '../components/Icon.jsx';
import ErrorState from '../components/ErrorState.jsx';
import StopsView, { eventsForStop } from './Stops.jsx';
import Diary, { AddVisit } from './Diary.jsx';
import SwipeToDelete from '../components/Swipe.jsx';

// A trip is a shape before it is a schedule. Opening on today answered "what is
// next" to someone who had not yet been told what the trip IS — five cities, two
// countries, nineteen days — so the stops lead now, and the clock lives one tab
// along with the plan and the diary beside it. Those three are the same trip on
// the same axis: what is happening, what is meant to, what did.
const TABS = [
  { id: 'viaggio', label: 'Viaggio', icon: 'pin' },
  { id: 'giorni',  label: 'Giorni',  icon: 'today' },
  { id: 'doc',     label: 'Doc',     icon: 'clip' },
  { id: 'costi',   label: 'Costi',   icon: 'euro' },
  { id: 'info',    label: 'Info',    icon: 'more' },
];

const DAY_MS = 86400000;

// A countdown to a gate has to stay true without a reload, and half a minute is
// close enough for a day measured in hours. The visibility listener is the part
// that matters in practice: the phone goes in a pocket for a seven-hour flight
// and has to come back showing the right thing, not the time it was put away.
function useNow(period = 30000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = () => setNow(Date.now());
    const id = setInterval(tick, period);
    const onWake = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onWake);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onWake); };
  }, [period]);
  return now;
}

export default function Trip() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('viaggio');
  // Which of the three time lenses is showing. Held here rather than in the tab
  // so that "tutto l'itinerario" from today can land on the plan directly.
  const [lens, setLens] = useState('oggi');
  // Shared across tabs on purpose: opening the notes to read the day, then
  // switching to the plan, should not close them again.
  const [notesOpen, setNotesOpen] = useState(false);
  const now = useNow();

  const reload = () => {
    setError(null);
    return fetchTrip(id).then((d) => {
      // Every amount on every screen is formatted in this, so it is set before
      // anything renders rather than threaded through nine components.
      setHomeCurrency(d.trip.home_currency);
      setData(d);
    }).catch(setError);
  };
  useEffect(() => { reload(); }, [id]);

  // Split in two on purpose: the shape of the trip does not change as the clock
  // moves, and rebuilding it every half minute would re-render every card.
  const plan = useMemo(() => {
    if (!data) return null;
    const { days, unscheduled } = buildDays(data.trip, data.events, data.dayNotes, data.visits);
    const priced = data.events.filter((e) => e.total_home != null);
    return {
      days, unscheduled, priced,
      // The tappe lens needs legs to tell where a journey starts and ends.
      legsBy: legsByParent(data.events),
      total: priced.reduce((s, e) => s + Number(e.total_home), 0),
      unresolved: data.events.filter((e) => e.decided === false),
      expected: data.documents.filter((d) => d.status === 'expected'),
      present: data.documents.filter((d) => d.status === 'present'),
      openTasks: data.checklist.filter((c) => !c.done),
    };
  }, [data]);

  const live = useMemo(() => {
    if (!data) return null;
    // Which day it is depends on where the traveller is, not on where the phone
    // last synced its clock.
    const zone = travellerZone(data.events, now);
    const today = zoneDate(now, zone);
    const soon = zoneDate(now + 14 * DAY_MS, zone);
    // Most countries want six months of passport validity beyond arrival, so
    // that is the line at which an expiry stops being a date and starts being a
    // problem.
    const soonish = zoneDate(now + 183 * DAY_MS, zone);
    return {
      zone, today, now, soonish,
      sched: buildToday(data, today, zone, now),
      // Only deadlines that are actually near are worth interrupting for.
      dueSoon: data.deadlines
        .filter((d) => !d.done && d.due_at.slice(0, 10) <= soon)
        .sort((a, b) => a.due_at.localeCompare(b.due_at)),
    };
  }, [data, now]);

  const model = plan && live ? { ...plan, ...live } : null;

  // The chrome stays up even when the content fails, so there is always a way
  // back to the trip list instead of a dead screen.
  if (error || !data || !model) {
    return (
      <div className="shell">
        <div className="topbar">
          <button className="iconbtn" onClick={() => nav('/')} aria-label="Tutti i viaggi">
            <Icon name="chevronLeft" size={17} />
          </button>
          <div className="grow"><h1>Viaggio</h1></div>
        </div>
        <div className="page">
          {error
            ? <ErrorState error={error} onRetry={reload} onBack={() => nav('/')} />
            : <div className="spinner" aria-label="Caricamento" />}
        </div>
      </div>
    );
  }

  const { trip } = data;
  const attention = model.unresolved.length + model.dueSoon.length;

  return (
    <div className="shell">
      <div className="topbar">
        <button className="iconbtn" onClick={() => nav('/')} aria-label="Tutti i viaggi">
          <Icon name="chevronLeft" size={17} />
        </button>
        <div className="grow">
          <h1>{trip.title}</h1>
          <p className="sub">
            {rangeLabel(trip.start_date, trip.end_date)}
            {trip.destination ? ` · ${trip.destination}` : ''}
          </p>
        </div>
      </div>

      <div className="page">
        <NotesOpen.Provider value={notesOpen}>
          {tab === 'viaggio' && <StopsView data={data} model={model} onDocChanged={reload}
                                           onChange={reload} />}
          {tab === 'giorni' && <Time data={data} model={model} onDocChanged={reload}
                                     onChange={reload} lens={lens} setLens={setLens}
                                     notesOpen={notesOpen} onToggleNotes={() => setNotesOpen((v) => !v)} />}
          {tab === 'doc' && <Documents data={data} model={model} onDocChanged={reload} />}
          {tab === 'costi' && <Costs data={data} model={model} />}
          {tab === 'info' && <Info data={data} model={model} onChange={reload} />}
        </NotesOpen.Provider>
      </div>

      <nav className="tabbar" role="tablist" aria-label="Sezioni">
        {TABS.map((t) => {
          const badge = t.id === 'giorni' ? attention
            : t.id === 'doc' ? model.expected.length : 0;
          return (
            <button key={t.id} className="tab" role="tab"
                    aria-selected={tab === t.id} onClick={() => setTab(t.id)}>
              <span className="ico">
                <Icon name={t.icon} size={19} />
                {badge > 0 && <span className="badge">{badge}</span>}
              </span>
              {t.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

/* ── Oggi ────────────────────────────────────────────────────────────────── */
//  Today, step by step, on the clock of wherever the traveller actually is.
//  Someone opening this between two flights wants the next thing that happens
//  and how long they have — problems come second, compactly.

// A stay is titled for the arrival — "· check-in" — which is the wrong half of
// it when the step is the departure.
const stayName = (e) =>
  e.place?.name ?? e.title.replace(/\s*·\s*check-?in\s*$/i, '');

// What a step is called. A wait and a checkout have no event of their own to
// name them: one is the space between two legs, the other the far end of a stay.
const stepTitle = (s) =>
  s.type === 'wait' ? `Scalo a ${s.place?.name ?? zoneLabel(s.zone)}`
    : s.type === 'end' ? `Uscita da ${stayName(s.event)}`
    : s.event.title;

// Every card can be opened on its own; this opens all of them, which is the
// only way to read a day rather than skim it.

export function NotesToggle({ open, onToggle }) {
  return (
    <div className="diarytools">
      <button className="act" aria-expanded={open} onClick={onToggle}>
        <Icon name="chevronRight" size={13} style={{ rotate: open ? '90deg' : '0deg' }} />
        {open ? 'Chiudi tutto' : 'Apri tutto'}
      </button>
    </div>
  );
}

// A step needs a name in the DOM so the band above can jump to it. The key
// carries a colon, which is legal in an id but awkward in a selector.
const stepId = (key) => `step-${String(key).replace(/[^\w-]/g, '_')}`;

function Overview({ data, model, onDocChanged, onChange, goPlan, notesOpen, onToggleNotes }) {
  const [adding, setAdding] = useState(false);
  const { trip } = data;
  const { sched, zone, now, today } = model;
  const beforeTrip = trip.start_date && today < trip.start_date;
  const afterTrip = trip.end_date && today > trip.end_date;

  // Off-trip there is no "today" to show, so the next day with something on it
  // stands in — the itinerary shape, not the live clock.
  const nextDay = model.days.find((d) => d.date > today && d.events.length > 0);
  const preview = sched.steps.length === 0 && nextDay ? nextDay : null;

  // On a moving day the traveller is in two places, and naming only the one they
  // are heading for is how the screen came to claim they were already there.
  const leaving = data.stops.find((s) => s.depart_date === today);
  const arriving = data.stops.find((s) => s.arrive_date === today);
  const stop = stopForDate(data.stops, today);
  const where = leaving && arriving && leaving.id !== arriving.id
    ? `${leaving.name} → ${arriving.name}`
    : (stop ?? arriving ?? leaving)?.name ?? null;

  const cur = sched.current;
  const here = cur?.type === 'wait' ? (cur.place?.name ?? zoneLabel(cur.zone))
    : cur?.event.kind === 'transport' ? `in volo verso ${zoneLabel(cur.endZone)}`
    : cur?.event.place?.name ?? where;

  return (
    <>
      <div className="today-head">
        <p className="kicker">
          {beforeTrip ? `Si parte fra ${Math.round((Date.parse(trip.start_date) - now) / DAY_MS)} giorni`
            : afterTrip ? 'Viaggio concluso'
            : <>Oggi <span className="clock">{zoneTime(now, zone)}</span> ora di {zoneLabel(zone)}</>}
        </p>
        <h2>{dayLabel(preview ? preview.date : today)}</h2>
        <p className="where">
          {dayOfWeek(preview ? preview.date : today)}
          {here && !preview && <><span style={{ color: 'var(--line-strong)' }}>·</span>
            <Icon name="pin" size={13} />{here}</>}
        </p>
      </div>

      {!preview && !beforeTrip && !afterTrip
        && <NowBand sched={sched} now={now} onDocChanged={onDocChanged} />}

      {sched.note && (
        <Block block={{ type: 'note', title: sched.note.title, content: { text: sched.note.body } }} />
      )}

      {preview
        ? (
          <>
            <p className="day-empty">Oggi niente in programma. La prossima giornata:</p>
            <div className="stack">
              {preview.events.map((e) => (
                <EventCard key={e.id} event={e} legs={e.dayLegs} onDocChanged={onDocChanged} />
              ))}
            </div>
          </>
        )
        : sched.steps.length === 0
          ? <p className="day-empty">Niente in programma.</p>
          : (
            <>
              <NotesToggle open={notesOpen} onToggle={onToggleNotes} />
              <ol className="tl">
                {sched.steps.map((s) => (
                  <Step key={s.key} s={s} sched={sched} now={now} onDocChanged={onDocChanged} />
                ))}
              </ol>
            </>
          )}

      {!beforeTrip && !afterTrip && (
        adding
          ? <AddVisit trip={trip} zone={zone} today={today}
                      onDone={() => setAdding(false)} onChange={onChange} />
          : (
            <button className="act addvisit-open" onClick={() => setAdding(true)}>
              <Icon name="plus" size={14} /> Segna qualcosa che avete fatto
            </button>
          )
      )}

      {sched.dayBlocks.map((b) => <Block key={b.id} block={b} />)}

      {(preview || afterTrip) && (
        <button className="act" style={{ marginTop: 12 }} onClick={goPlan}>
          Tutto l'itinerario <Icon name="chevronRight" size={14} />
        </button>
      )}

      {(model.unresolved.length > 0 || model.dueSoon.length > 0) && (
        <section className="section">
          <h2>Da sistemare <span className="count">{model.unresolved.length + model.dueSoon.length}</span></h2>
          <div className="rows">
            {model.unresolved.map((e) => (
              <div className="row" key={e.id}>
                <span style={{ color: 'var(--attn)', flex: 'none', marginTop: 2 }}>
                  <Icon name="alert" size={16} />
                </span>
                <span className="body">
                  <b>{e.title}</b>
                  <small>
                    Doppia prenotazione il {dayShort(e.event_date)}
                    {e.provider ? ` · ${e.provider}` : ''} · una va cancellata
                  </small>
                </span>
                {e.total_home != null && (
                  <span className="trail"><b>{money(e.total_home)}</b></span>
                )}
              </div>
            ))}
            {model.dueSoon.map((d) => (
              <button className="row tappable" key={d.id}
                      onClick={async () => { await setDeadlineDone(d.id, true); onChange(); }}>
                <span className="check"><Icon name="check" size={12} /></span>
                <span className={`when${d.due_at.slice(0, 10) <= today ? ' due' : ''}`}>
                  {d.due_at.slice(0, 10) === today ? 'oggi'
                    : d.due_at.slice(0, 10) < today ? 'scaduta'
                    : dayShort(d.due_at.slice(0, 10))}
                </span>
                <span className="body">
                  <b>{d.title}</b>
                  {d.detail && <small>{d.detail}</small>}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="section">
        <div className="stats">
          <div className="stat">
            <b>{money(model.total)}</b>
            <span>costo totale</span>
          </div>
          <div className="stat">
            <b>{model.present.length}<span className="of">/{data.documents.length}</span></b>
            <span>documenti</span>
          </div>
          <div className={`stat${model.openTasks.length ? ' attn' : ''}`}>
            <b>{model.openTasks.length}</b>
            {/* Once you are on the road "prima di partire" is simply untrue. */}
            <span>{beforeTrip ? 'prima di partire' : 'da fare'}</span>
          </div>
          <div className="stat">
            <b>{data.stops.length}</b>
            <span>tappe</span>
          </div>
        </div>
      </section>
    </>
  );
}

// The one thing worth reading first: what you are in the middle of, how much of
// it is left, and what comes after it. Everything else on the screen is detail.
function NowBand({ sched, now, onDocChanged }) {
  const cur = sched.current;
  const next = sched.next;
  // Whatever the band is talking about is the thing whose ticket you are about
  // to be asked for. It was already on the card further down the page; needing
  // to scroll for it while a queue moves is exactly the wrong moment.
  const subject = cur ?? next;
  const docs = subject?.docs ?? subject?.event?.documents ?? [];
  const jump = () => {
    if (!subject) return;
    const el = document.getElementById(stepId(subject.key));
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // A scroll on a long day lands you somewhere plausible-looking; the flash
    // says which of the cards you were actually sent to.
    el.classList.add('flash');
    setTimeout(() => el.classList.remove('flash'), 1400);
  };
  const done = sched.steps.length > 0 && sched.steps.every((s) => s.done);

  let icon = 'today';
  let head = null;
  let sub = null;

  if (cur?.type === 'wait') {
    icon = 'clock';
    head = stepTitle(cur);
    sub = <>ancora <b>{spanLabel(cur.until - now)}</b> · scalo di {spanLabel(cur.until - cur.at)}</>;
  } else if (cur && cur.event.kind === 'transport') {
    icon = 'plane';
    head = cur.event.title;
    sub = cur.until
      ? <>arrivo alle <b>{zoneTime(cur.until, cur.endZone)}</b> ora di {zoneLabel(cur.endZone)}
          {' · '}{untilLabel(cur.until - now)}</>
      : 'in viaggio';
  } else if (cur) {
    icon = KIND_ICON[cur.event.kind] ?? 'today';
    head = cur.event.title;
    sub = cur.until && cur.event.kind !== 'lodging'
      ? <>fino alle <b>{zoneTime(cur.until, cur.endZone)}</b></>
      : (KIND[cur.event.kind]?.label ?? 'in corso').toLowerCase();
  } else if (next) {
    icon = KIND_ICON[next.event.kind] ?? 'today';
    head = stepTitle(next);
    sub = <>alle <b>{zoneTime(next.at, next.zone)}</b> · {untilLabel(next.at - now)}</>;
  } else if (done) {
    head = 'Giornata conclusa';
    sub = 'niente altro in programma oggi';
  } else {
    return null;
  }

  return (
    <div className="nowband">
      <p className="lbl"><Icon name={icon} size={13} />{cur ? 'Adesso' : next ? 'Prossimo passo' : 'Oggi'}</p>
      {subject
        ? (
          <button className="jump" onClick={jump}>
            <h3>{head}</h3>
            <Icon name="chevronRight" size={16} />
          </button>
        )
        : <h3>{head}</h3>}
      <p className="sub">{sub}</p>
      {cur && next && (
        <p className="then">
          Poi <b>{stepTitle(next)}</b> alle {zoneTime(next.at, next.zone)}
          {next.zone !== cur.zone ? ` ora di ${zoneLabel(next.zone)}` : ''}
          {' · '}{untilLabel(next.at - now)}
        </p>
      )}
      {docs.length > 0 && (
        <div className="docs-inline">
          {docs.map((d) => <DocRow key={d.id} doc={d} onChanged={onDocChanged} />)}
        </div>
      )}
    </div>
  );
}

// One step on the rail. The time lives in the gutter so the card beside it does
// not repeat it, and it is printed on the clock of the place the step happens
// in — a boarding pass says 10:25 in Dubai, so the screen has to as well. Where
// that clock is not the one the traveller is reading, it is labelled.
function Step({ s, sched, now, onDocChanged }) {
  const e = s.event;
  const otherZone = s.zone && s.zone !== sched.zone;
  const stepDate = s.at == null ? null : zoneDate(s.at, s.zone);
  const dateHint = stepDate && stepDate !== sched.date
    ? (stepDate < sched.date ? 'ieri' : 'domani') : null;

  // A leg carries the flight; the booking that covers it carries the boarding
  // passes. On the day itself they belong to the same step.
  const event = s.docs ? { ...e, documents: s.docs } : e;

  return (
    <li id={stepId(s.key)} className={`step${s.done ? ' done' : ''}${s.live ? ' live' : ''}`}>
      <div className="when">
        <span className="t">{s.at == null ? '—' : zoneTime(s.at, s.zone)}</span>
        {dateHint && <span className="z">{dateHint}</span>}
        {otherZone && <span className="z">{zoneLabel(s.zone)}</span>}
      </div>

      <div className="what">
        {/* Neither a layover nor a checkout is a booking of its own, so neither
            gets a card: they are the joins between the things that are. */}
        {s.type === 'visit' ? (
          <SwipeToDelete prompt={`Togliere «${s.visit.title}» dal diario?`}
                         onDelete={async () => { await deleteVisit(s.visit); await onDocChanged?.(); }}>
            <VisitCard visit={s.visit} onChanged={onDocChanged} />
          </SwipeToDelete>
        ) : s.type === 'wait' ? (
          <div className="plain">
            <b>{stepTitle(s)}</b>
            <small>
              {spanLabel(s.until - s.at)} di attesa
              {s.live ? ` · ancora ${spanLabel(s.until - now)}` : ''}
              {s.from?.details?.terminal ? ` · ${s.from.details.terminal}` : ''}
            </small>
          </div>
        ) : s.type === 'end' ? (
          <div className="plain">
            <b>{stepTitle(s)}</b>
            <small>fine del soggiorno</small>
          </div>
        ) : (
          <SwipeToDelete prompt={deletePrompt(event)}
                         onDelete={async () => { await deleteEvent(event); await onDocChanged?.(); }}>
            <EventCard event={event} legs={[]} hideTime onDocChanged={onDocChanged}
                       onDelete={async () => { await deleteEvent(event); await onDocChanged?.(); }} />
          </SwipeToDelete>
        )}
      </div>
    </li>
  );
}

/* ── Giorni · two lenses on the same trip ────────────────────────────────── */
//  Time answers "what happens now". Place answers "we are here for three days,
//  what do we have". Both are the itinerary; neither is a subsection of the
//  other, so they sit side by side rather than one being buried in a menu.

// Now, the plan, and what became of it. Three answers to "when", so they are
// three lenses rather than three tabs — and the diary earns its place here
// because an entry with no plan behind it is already on the day beside the
// things that were planned.
function Time({ data, model, onDocChanged, onChange, lens, setLens, notesOpen, onToggleNotes }) {
  return (
    <>
      <div className="segmented" role="tablist" aria-label="Vista">
        <button role="tab" aria-selected={lens === 'oggi'} onClick={() => setLens('oggi')}>
          Oggi
        </button>
        <button role="tab" aria-selected={lens === 'piano'} onClick={() => setLens('piano')}>
          Piano
        </button>
        <button role="tab" aria-selected={lens === 'diario'} onClick={() => setLens('diario')}>
          Diario
        </button>
      </div>
      {lens === 'oggi' && (
        <Overview data={data} model={model} onDocChanged={onDocChanged} onChange={onChange}
                  goPlan={() => setLens('piano')}
                  notesOpen={notesOpen} onToggleNotes={onToggleNotes} />
      )}
      {lens === 'piano' && (
        <Days data={data} model={model} onDocChanged={onDocChanged}
              notesOpen={notesOpen} onToggleNotes={onToggleNotes} />
      )}
      {lens === 'diario' && <Diary data={data} onChange={onChange} />}
    </>
  );
}

// Only what a chip can honestly move. A flight and a hotel carry legs, nights
// and a confirmation that all have to move with them — that is a conversation
// with the airline, not a tap.
const movable = (e, days, zone, onChanged) =>
  ['activity', 'meal'].includes(e.kind)
    ? {
      moveDays: days,
      moveZone: zone,
      onMove: async (patch) => { await moveEvent(e, patch); await onChanged?.(); },
    }
    : {};

function Days({ data, model, onDocChanged, notesOpen, onToggleNotes }) {
  const allDays = model.days.map((d) => d.date);

  return (
    <>
      {model.days.some((d) => d.events.length > 0) && (
        <NotesToggle open={notesOpen} onToggle={onToggleNotes} />
      )}
      {model.days.map((day) => {
        const stop = stopForDate(data.stops, day.date);
        const isToday = day.date === model.today;
        // A day is a route from where you slept through everything on it, so the
        // hotel leads even on the days it is not itself an entry.
        const home = stop
          ? eventsForStop(stop, data.events, model.legsBy)
              .find((e) => e.kind === 'lodging' && e.place)?.place ?? null
          : null;
        const route = mappablePlaces([home, ...day.events.map((e) => e.place)]);
        // A flight's zone is where it takes off, which on a moving day is the
        // place you are leaving — the wrong clock to book an afternoon in.
        const dayZone = (day.events.find((e) => e.kind !== 'transport' && e.timezone)
          ?? day.events.find((e) => e.timezone))?.timezone ?? null;

        return (
          <section key={day.date}>
            <div className={`day-h${isToday ? ' today' : ''}`}>
              <b>{dayLabel(day.date)}</b>
              <span className="dow">{dayOfWeek(day.date)}</span>
              {stop && <span className="where">{stop.name}</span>}
              {/* One place is already a pin on its own card; two or more is a
                  question about distance that only a map answers. */}
              {route.length > 1 && (
                <a className="act" href={mapsRouteHref(route)} target="_blank" rel="noreferrer"
                   aria-label={`La mappa del ${dayLabel(day.date)}`}>
                  <Icon name="pin" size={13} /> Mappa
                </a>
              )}
            </div>

            {day.note && (
              <Block block={{ type: 'note', title: day.note.title, content: { text: day.note.body } }} />
            )}

            {day.events.length === 0 && !day.note && <p className="day-empty">Giornata libera</p>}

            <div className="stack">
              {day.events.map((e) => (e.__visit
                ? (
                  <SwipeToDelete key={e.id} prompt={`Togliere «${e.title}» dal diario?`}
                                 onDelete={async () => { await deleteVisit(e); await onDocChanged?.(); }}>
                    <VisitCard visit={e} time={localTime(e)} onChanged={onDocChanged} />
                  </SwipeToDelete>
                )
                : (
                  <SwipeToDelete key={e.id} prompt={deletePrompt(e, e.dayLegs ?? [])}
                                 onDelete={async () => { await deleteEvent(e); await onDocChanged?.(); }}>
                    <EventCard event={e} legs={e.dayLegs}
                               dayHint={e.startedOn ? `iniziato il ${dayShort(e.startedOn)}`
                                 : e.continuesOn ? `prosegue il ${dayShort(e.continuesOn)}` : null}
                               hideTime={!!e.startedOn}
                               {...movable(e, allDays, dayZone, onDocChanged)}
                               onDocChanged={onDocChanged}
                               onDelete={async () => { await deleteEvent(e); await onDocChanged?.(); }} />
                  </SwipeToDelete>
                )
              ))}
              {data.blocks
                .filter((b) => b.parent_type === 'day' && b.parent_date === day.date)
                .map((b) => <Block key={b.id} block={b} />)}
            </div>
          </section>
        );
      })}

      {model.unscheduled.length > 0 && (
        <section className="section">
          <h2>Da collocare <span className="count">{model.unscheduled.length}</span></h2>
          <p className="lead">Già nel totale, ma senza una data.</p>
          <div className="stack">
            {model.unscheduled.map((e) => (
              <SwipeToDelete key={e.id} prompt={deletePrompt(e)}
                             onDelete={async () => { await deleteEvent(e); await onDocChanged?.(); }}>
                <EventCard event={e} onDocChanged={onDocChanged}
                           onDelete={async () => { await deleteEvent(e); await onDocChanged?.(); }} />
              </SwipeToDelete>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

/* ── Documenti ───────────────────────────────────────────────────────────── */

function Documents({ data, model, onDocChanged }) {
  const byKind = (list) => {
    const groups = new Map();
    for (const d of list) {
      const k = d.kind ?? 'other';
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(d);
    }
    return [...groups.entries()];
  };
  const LABEL = {
    ticket: 'Biglietti', boarding_pass: 'Carte d\'imbarco', reservation: 'Prenotazioni',
    voucher: 'Voucher', receipt: 'Ricevute', visa: 'Visti', insurance: 'Assicurazione',
    agreement: 'Accordi', id: 'Documenti d\'identità', menu: 'Menù', other: 'Altro',
  };

  return (
    <>
      {model.expected.length > 0 && (
        <section className="section">
          <h2>Da caricare <span className="count">{model.expected.length}</span></h2>
          <p className="lead">Lo spazio è già pronto e collegato alla prenotazione giusta.</p>
          <div className="rows">
            {byKind(model.expected).map(([kind, list]) => (
              <div className="row" key={kind} style={{ display: 'block' }}>
                <small style={{ color: 'var(--faint)', marginBottom: 2 }}>{LABEL[kind] ?? kind}</small>
                <div className="docs-inline" style={{ marginTop: 0 }}>
                  {list.map((d) => <DocRow key={d.id} doc={d} onChanged={onDocChanged} />)}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="section">
        <h2>Archivio <span className="count">{model.present.length}</span></h2>
        {model.present.length === 0
          ? <p className="lead">Ancora nessun file caricato.</p>
          : (
            <div className="rows">
              {byKind(model.present).map(([kind, list]) => (
                <div className="row" key={kind} style={{ display: 'block' }}>
                  <small style={{ color: 'var(--faint)', marginBottom: 2 }}>{LABEL[kind] ?? kind}</small>
                  <div className="docs-inline" style={{ marginTop: 0 }}>
                    {list.map((d) => <DocRow key={d.id} doc={d} onChanged={onDocChanged} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
      </section>
    </>
  );
}

/* ── Costi ───────────────────────────────────────────────────────────────── */

function Costs({ data, model }) {
  const rows = [...model.priced].sort((a, b) => b.total_home - a.total_home);
  const onSite = data.events.filter((e) =>
    ['to_settle', 'due_on_site', 'deposit_paid'].includes(e.payment_status));
  const noPrice = data.events.filter((e) =>
    e.total_home == null && !e.parent_id
    && ['transport', 'lodging', 'activity'].includes(e.kind));

  return (
    <>
      <div className="today-head">
        <p className="kicker">Costo del viaggio</p>
        <h2>{money(model.total, { cents: true })}</h2>
        <p className="where">
          {model.priced.length} voci, pagate o no
          {Object.keys(data.rates).length > 0
            && ` · ${Object.entries(data.rates).map(([c, r]) => `${c} ${r}`).join(', ')} per €`}
        </p>
      </div>

      <div className="rows">
        {rows.map((e) => (
          <div className="row" key={e.id}>
            <span className="body">
              <b>{e.title}</b>
              <small>{[e.provider ?? e.vendor?.name, derivation(e)].filter(Boolean).join(' · ')}</small>
            </span>
            <span className="trail">
              <b>{money(e.total_home, { cents: true })}</b>
              {e.payment_status && <small>{PAYMENT[e.payment_status]?.label}</small>}
            </span>
          </div>
        ))}
      </div>

      {onSite.length > 0 && (
        <section className="section">
          <h2>Da regolare sul posto <span className="count">{onSite.length}</span></h2>
          <p className="lead">Già nel totale. Elencati perché servono contanti.</p>
          <div className="rows">
            {onSite.map((e) => (
              <div className="row" key={e.id}>
                <span className="body">
                  <b>{e.title}</b>
                  {e.payment_terms && <small>{e.payment_terms}</small>}
                </span>
                {e.total_local != null && (
                  <span className="trail"><b>{localAmount(e.total_local, e.currency_local)}</b></span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {noPrice.length > 0 && (
        <section className="section">
          <h2>Senza importo <span className="count">{noPrice.length}</span></h2>
          <p className="lead">Non presenti nelle conferme.</p>
          <div className="rows">
            {noPrice.map((e) => (
              <div className="row" key={e.id}><span className="body"><b>{e.title}</b></span></div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

/* ── Info ────────────────────────────────────────────────────────────────── */

// What is left once the reading matter has a home of its own: the things you
// DO to a trip rather than read about it. The story and the trip's notes moved
// to Viaggio, where the trip is described; nothing here is about the places.
function Info({ data, model, onChange }) {
  const { trip } = data;

  return (
    <>
      {data.checklist.length > 0 && (
        <section className="section">
          <h2>Prima di partire <span className="count">{model.openTasks.length} da fare</span></h2>
          <div className="rows">
            {data.checklist.map((c) => (
              <button className={`row tappable${c.done ? ' done' : ''}`} key={c.id}
                      onClick={async () => { await setChecklistDone(c.id, !c.done); onChange(); }}>
                <span className="check"><Icon name="check" size={12} /></span>
                <span className="body">
                  <b>{c.title}</b>
                  {(c.due_date || c.per_traveler) && (
                    <small style={{ color: c.due_date && c.due_date <= model.today ? 'var(--alert)' : 'var(--muted)' }}>
                      {[c.due_date ? `entro il ${dayShort(c.due_date)}` : null,
                        c.per_traveler ? 'per ogni viaggiatore' : null].filter(Boolean).join(' · ')}
                    </small>
                  )}
                  {c.detail && <small>{c.detail}</small>}
                  {c.url && <small style={{ color: 'var(--brand)' }}>{c.url.replace(/^https?:\/\//, '')}</small>}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {data.travelers.length > 0 && (
        <section className="section">
          <h2>Chi viaggia <span className="count">{data.travelers.length}</span></h2>
          {/* Passport numbers and expiries have been written on every trip since
              the first import and displayed nowhere. They are the reason the
              field exists: an expiry six months out is a problem you want to
              find at home, not at a desk. */}
          <div className="rows">
            {data.travelers.map((t) => {
              const expiring = t.passport_expiry && t.passport_expiry < model.soonish;
              return (
                <div className="row" key={t.id}>
                  <span className="body">
                    <b>{t.name}</b>
                    <small>
                      {[ageOn(t.birthdate, model.today) != null
                          ? `${ageOn(t.birthdate, model.today)} anni` : null,
                        t.nationality,
                        t.passport_expiry ? `passaporto fino al ${dayShort(t.passport_expiry)}` : null,
                        t.passport_number].filter(Boolean).join(' · ')}
                    </small>
                    {t.notes && <small>{t.notes}</small>}
                  </span>
                  {expiring && <span className="trail"><span className="state attn">in scadenza</span></span>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {data.contacts.length > 0 && (
        <section className="section">
          <h2>Contatti</h2>
          <div className="rows">
            {data.contacts.map((c) => (
              <div className="row" key={c.id}>
                <span className="body">
                  <b>{c.label}</b>
                  {c.note && <small>{c.note}</small>}
                </span>
                {c.phone && (
                  <a className="act" href={telHref(c.phone)}><Icon name="phone" size={14} /></a>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <Sharing trip={trip} />

    </>
  );
}

/* ── who else sees this trip ─────────────────────────────────────────────── */
//  Trip-level, so it lives in the trip rather than in a settings screen: what
//  is being shared is this itinerary, not the account. Inviting is a proposal —
//  the row exists but grants nothing until the other person accepts — so an
//  unanswered invitation is shown as "in attesa" instead of as a member, or an
//  owner reads silence as agreement.

function Sharing({ trip }) {
  const [members, setMembers] = useState(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const key = trip.external_key ?? trip.id;

  const load = () => fetchMembers(key).then(setMembers).catch(() => setMembers([]));
  useEffect(() => { load(); }, [key]);

  const invite = async (e) => {
    e.preventDefault();
    const address = email.trim();
    if (!address) return;
    setBusy(true); setMsg(null);
    try {
      await inviteMember(key, address, role);
      setEmail('');
      setMsg({ ok: true, text: `Invito inviato a ${address}. Comparirà qui come «in attesa» finché non accetta.` });
      load();
    } catch (err) {
      setMsg({ ok: false, text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const drop = async (m) => {
    setBusy(true); setMsg(null);
    try {
      await removeMember(key, m.email);
      load();
    } catch (err) {
      setMsg({ ok: false, text: err.message });
    } finally {
      setBusy(false);
    }
  };

  if (!members) return null;

  return (
    <section className="section">
      <h2>Chi vede questo viaggio</h2>

      <div className="rows">
        {members.map((m) => (
          <div className="row" key={m.email}>
            <span className="body">
              <b>{m.email}</b>
              <small>
                {m.role === 'owner' ? 'proprietario' : m.role === 'viewer' ? 'sola lettura' : 'può modificare'}
                {m.status === 'invited' && ' · in attesa di accettare'}
              </small>
            </span>
            {m.role !== 'owner' && (
              <button className="act" disabled={busy} aria-label={`Togli ${m.email}`}
                      onClick={() => drop(m)}>
                <Icon name="trash" size={14} />
              </button>
            )}
          </div>
        ))}
      </div>

      <form className="invite-form" onSubmit={invite}>
        <input type="email" inputMode="email" autoComplete="off"
               placeholder="indirizzo email" value={email}
               onChange={(e) => setEmail(e.target.value)} />
        <select value={role} onChange={(e) => setRole(e.target.value)} aria-label="Permessi">
          <option value="editor">può modificare</option>
          <option value="viewer">sola lettura</option>
        </select>
        <button className="btn" type="submit" disabled={busy || !email.trim()}>Invita</button>
      </form>

      {msg && <p className={msg.ok ? 'hint' : 'hint bad'} role="status">{msg.text}</p>}

      <p className="hint">
        Deve essersi già collegato una volta all'app, così l'account esiste.
        L'invito non fa nulla finché non lo accetta.
      </p>
    </section>
  );
}
